/**
 * @fileoverview BrowserInstructionsRenderer component for the Storyteller web application.
 * Displays browser instructions and icons based on the detected browser.
 *
 * @example
 * // Basic usage
 * <BrowserInstructionsRenderer />
 */

import { LabelProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsLabelProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import {
    BotInfo,
    BrowserInfo,
    NodeInfo,
    ReactNativeInfo,
    SearchBotDeviceInfo,
    detect,
} from 'detect-browser'
import config from '../../../../config'
import { BoldCaption } from '../../../typography/Typography'
import { Instructions } from '../../sections/Instructions'

import { useTranslation } from 'react-i18next'
import css from './BrowserInstructionsRenderer.module.css'

const mobilePlatforms = ['iOS', 'Android OS', 'BlackBerry OS', 'Windows Mobile']

/**
 * @function isSupported
 * @description Checks if the browser is supported.
 * @param {string} name - The name of the browser.
 * @returns {boolean} True if the browser is supported, false otherwise.
 */
function isSupported(name: string): boolean {
    if (!name) {
        return false
    }

    return ['chrome', 'safari', 'edge', 'edge-chromium', 'firefox', 'opera'].indexOf(name) > -1
}

/**
 * @function getIcon
 * @description Returns the icon position for the browser based on the browser name.
 * @param {string} icon - The name of the browser.
 * @returns {string} The icon position for the browser.
 */
function getIcon(icon: string) {
    switch (icon) {
        case 'chrome':
            return '0px 0px'
        case 'safari':
            return 'calc(-32px - 24px) 0px'
        case 'edge':
        case 'edge-chromium':
            return 'calc(-32px*2 - 24px*2) 0px'
        case 'firefox':
            return 'calc(-32px*3 - 24px*3) 0px'
        case 'opera':
            return 'calc(-32px*4 - 24px*4) 0px'
        case 'ios':
            return 'calc(-32px*5 - 24px*5) 0px'
        case 'android':
            return 'calc(-32px*6 - 24px*6) 0px'
    }
}

/**
 * @function getBrowserName
 * @description Returns the name of the browser based on the detected browser.
 * @param {BrowserInfo | SearchBotDeviceInfo | BotInfo | NodeInfo | ReactNativeInfo} browser - The detected browser.
 * @returns {string} The name of the browser.
 */
function getBrowserName(
    browser: BrowserInfo | SearchBotDeviceInfo | BotInfo | NodeInfo | ReactNativeInfo
) {
    const name = browser.name
    if (/edge/s.exec(name)) {
        return 'edge'
    }
    return name
}

/**
 * @function BrowserInstructionsRenderer
 * @description Component for displaying browser instructions and icons.
 * @param {LabelProps & VanillaRendererProps} props - The props for the BrowserInstructionsRenderer component.
 * @returns {JSX.Element} The BrowserInstructionsRenderer component.
 */
function BrowserInstructionsRenderer({ uischema }: LabelProps & VanillaRendererProps) {
    const { t } = useTranslation()
    const browser = detect()
    let browserName = browser && isSupported(browser.name) ? getBrowserName(browser) : 'chrome'

    const isMobile = mobilePlatforms.indexOf(browser.os) > -1
    const isNewSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    if (isNewSafari) {
        browserName = isMobile ? 'ios' : 'safari'
    }

    const isiOS = browser.os === 'iOS'
    const { browsers, mobile } = uischema.options

    let copy = browsers[browserName]

    if (isMobile) {
        copy = mobile[isiOS ? 'ios' : 'android']
    }

    return (
        <>
            <div className={css.browser_title}>
                <img
                    alt="browser icon"
                    className={css.browser_icon}
                    style={{ objectPosition: getIcon(browserName) }}
                    src={config.s3.getStaticURL('browsers.svg')}
                />
                <BoldCaption>{t(copy.title)}</BoldCaption>
            </div>
            <Instructions instructions={t(copy.instructions)} />
        </>
    )
}

export const BrowserInstructionsTester = rankWith(3, uiTypeIs('BrowserInstructions'))

export default withVanillaControlProps(withJsonFormsLabelProps(BrowserInstructionsRenderer))
